import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["customStrings", "elementToFocusOnClose", "onClose", "onHandleCloseKeyboardHelp"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import React, { useEffect } from "react";
import { KEYBOARD_KEYS } from "../../../common/constants";
import { Button, Panel, Table } from "../../..";
import extractTheme from "./extractTheme";
import { createTableStrings } from "./utils";
/**
 * A functional component that returns a Panel containing
 * helpful keyboard commands for accessibility purposes.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function KeyboardHelpPanel(_ref) {
  let {
    customStrings,
    elementToFocusOnClose,
    onClose,
    onHandleCloseKeyboardHelp
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssTable,
    cssHeader,
    cssInstructions,
    cssCloseButtonContainer
  } = extractTheme({
    theme
  });
  const helpStrings = customStrings.keyboardHelp;
  const strings = createTableStrings(helpStrings);
  /* eslint-disable no-magic-numbers */

  const keyboardHelpInstructions = [...strings].splice(0, 6);
  const keyboardHelpRowsInstructions = [...strings].splice(6);
  /* eslint-enable no-magic-numbers */

  /**
   * Toggles the help panel's visibility on ALT+k.
   *
   * @param {Object} e - The `keydown` event object.
   */

  const handleKeyboardHelpPanel = e => {
    const {
      MAC_ALT_K,
      K
    } = KEYBOARD_KEYS;
    const key = e.key.toLowerCase();
    const {
      altKey
    } = e;

    if (key === MAC_ALT_K || altKey && key === K) {
      onHandleCloseKeyboardHelp(false);
    }
  };
  /**
   * Handles the `keydown` event on the Panel.
   *
   * @param {Object} e - The `keydown` event object.
   */


  const handleKeyDown = e => {
    handleKeyboardHelpPanel(e, false);
  };
  /**
   * Initialize and clean up event listeners.
   */


  useEffect(() => {
    // Add event listener when component mounts.
    document.addEventListener("keydown", handleKeyDown); // Remove event listener when component unmounts.

    return () => document.removeEventListener("keydown", handleKeyDown);
  });
  /**
   * Logic to run when closing this component via the
   * close button ("X").
   */

  const handleClose = () => {
    onClose();
  };
  /**
   * Logic to run when closing this component via the
   * Panel footer's "Close" button.
   */


  const handleCloseKeyboardHelp = () => {
    onHandleCloseKeyboardHelp(false);
  };

  const {
    action,
    close,
    navigationInstructions,
    panelShowHideInstructions,
    panelTitle,
    selectedInstructions,
    shortcutKey
  } = helpStrings;
  return _jsxs(Panel, _objectSpread(_objectSpread({
    bodyRole: "document",
    className: "pnr-keyboard-help-panel",
    customStrings: {
      closeButtonAriaLabel: customStrings.closeButtonAriaLabelKeyboardHelpPanel
    },
    customStyles: {
      header: cssHeader
    },
    elementToFocusOnClose: elementToFocusOnClose,
    onClose: handleClose,
    open: true,
    title: panelTitle,
    footer: _jsx("div", {
      css: cssCloseButtonContainer,
      children: _jsx(Button, {
        kind: "primary",
        label: close,
        onClick: handleCloseKeyboardHelp,
        "data-testid": "footer-close-button"
      })
    })
  }, otherProps), {}, {
    children: [_jsx("div", {
      children: navigationInstructions
    }), _jsx(Table, {
      className: "pnr-keyboard-help-panel__table",
      columns: [{
        key: "action",
        label: action
      }, {
        key: "shortcut",
        label: shortcutKey
      }],
      css: cssTable,
      data: keyboardHelpInstructions,
      rowKey: "action"
    }), _jsx("div", {
      css: cssInstructions,
      children: selectedInstructions
    }), _jsx(Table, {
      className: "pnr-keyboard-help-panel__table",
      columns: [{
        key: "action",
        label: action
      }, {
        key: "shortcut",
        label: shortcutKey
      }],
      css: cssTable,
      data: keyboardHelpRowsInstructions,
      rowKey: "action"
    }), _jsx("div", {
      css: cssInstructions,
      children: panelShowHideInstructions
    })]
  }));
}

KeyboardHelpPanel.displayName = "KeyboardHelpPanel";
export default KeyboardHelpPanel;